"use client";

import { useSyncExternalStore } from "react";
import { COOKIEYES_CONSENT_COOKIE, NO_TRACKING_CONSENT, parseCookieYesConsent, type TrackingConsent } from "@/utils/tracking/consent";
import { canLoadBrowserAdTags } from "@/utils/tracking/environment";

// CookieYes browser API: https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
interface CookieYesConsentState {
  categories?: Partial<Record<string, boolean>>;
}
declare global {
  interface Window {
    getCkyConsent?: () => CookieYesConsentState | undefined;
  }
}
const CONSENT_UPDATE_EVENT = "cookieyes_consent_update";
const BANNER_LOAD_EVENT = "cookieyes_banner_load";
let snapshot: TrackingConsent | null = null;
const listeners = new Set<() => void>();
let isListening = false;
function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const cookie = part.trim();
    if (cookie.startsWith(prefix)) return cookie.slice(prefix.length);
  }
  return null;
}
function readCurrentConsent(): TrackingConsent {
  if (typeof window === "undefined") return NO_TRACKING_CONSENT;
  try {
    const categories = window.getCkyConsent?.()?.categories;
    if (categories) {
      return {
        advertisement: categories.advertisement === true,
        analytics: categories.analytics === true
      };
    }
  } catch {
    // Fall back to the cookie CookieYes writes.
  }
  try {
    return parseCookieYesConsent(readCookie(COOKIEYES_CONSENT_COOKIE));
  } catch {
    return NO_TRACKING_CONSENT;
  }
}

// Every browser ad tag and ad event checks `advertisement` from here. Outside
// production it always reads as "no", so local dev and preview deployments
// load no ad pixel and send no ad event. Analytics consent is unaffected.
function withAdEnvironment(consent: TrackingConsent): TrackingConsent {
  if (!consent.advertisement || canLoadBrowserAdTags()) return consent;
  return {
    ...consent,
    advertisement: false
  };
}
function setConsent(update: TrackingConsent): void {
  const next = withAdEnvironment(update);
  if (snapshot && snapshot.advertisement === next.advertisement && snapshot.analytics === next.analytics) {
    return;
  }
  snapshot = next;
  listeners.forEach(listener => listener());
}
function handleConsentUpdate(event: Event): void {
  const accepted = (event as CustomEvent<{
    accepted?: unknown;
  }>).detail?.accepted;
  if (Array.isArray(accepted)) {
    setConsent({
      advertisement: accepted.includes("advertisement"),
      analytics: accepted.includes("analytics")
    });
    return;
  }
  setConsent(readCurrentConsent());
}
function handleBannerLoad(): void {
  setConsent(readCurrentConsent());
}
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!isListening) {
    document.addEventListener(CONSENT_UPDATE_EVENT, handleConsentUpdate);
    document.addEventListener(BANNER_LOAD_EVENT, handleBannerLoad);
    isListening = true;
  }
  return () => {
    listeners.delete(listener);
  };
}

/** Current consent, for event handlers outside React render. */
export function getTrackingConsent(): TrackingConsent {
  if (typeof window === "undefined") return NO_TRACKING_CONSENT;
  if (!snapshot) snapshot = withAdEnvironment(readCurrentConsent());
  return snapshot;
}
function getServerSnapshot(): TrackingConsent {
  return NO_TRACKING_CONSENT;
}

/**
 * CookieYes consent, updated live when the visitor accepts or rejects in the
 * banner. Server render and hydration always see "no consent".
 */
export function useTrackingConsent() {
  return useSyncExternalStore(subscribe, getTrackingConsent, getServerSnapshot);
}